/**
 * @fileoverview Estilos compartilhados da pagina de Templates Meta.
 *
 * Aesthetic: editorial-minimalism. Tipografia com hierarquia clara,
 * espacamento generoso, acentos precisos e suporte bem contrastado a
 * temas claro/escuro.
 */

import { makeStyles } from "@material-ui/core/styles";

const isDark = (theme) => theme.palette.type === "dark";

export const useListStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: theme.spacing(4),
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  headerLeft: {
    minWidth: 0,
  },
  eyebrow: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#64748b",
    marginBottom: theme.spacing(0.5),
  },
  title: {
    fontSize: "2rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
  },
  subtitle: {
    marginTop: theme.spacing(1),
    fontSize: "0.92rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#475569",
    maxWidth: 520,
    lineHeight: 1.5,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "10px 20px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontWeight: 600,
    textTransform: "none",
    color: "#FFFFFF",
    background: "linear-gradient(135deg, #0064ff 0%, #0462ca 100%)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      filter: "brightness(1.05)",
    },
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
      transform: "none",
    },
  },
  ghostButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "10px 16px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: "0.88rem",
    fontWeight: 500,
    background: "transparent",
    color: isDark(theme) ? "rgba(255,255,255,0.75)" : "#475569",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid rgba(15,23,42,0.12)",
    transition: "background 0.15s ease, color 0.15s ease",
    "&:hover": {
      background: isDark(theme) ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
  },
  connectionPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "8px 14px",
    borderRadius: 999,
    fontSize: "0.82rem",
    fontWeight: 500,
    background: isDark(theme) ? "rgba(99,102,241,0.12)" : "rgba(99,102,241,0.08)",
    color: isDark(theme) ? "#c7d2fe" : "#4338ca",
    border: isDark(theme)
      ? "1px solid rgba(99,102,241,0.25)"
      : "1px solid rgba(99,102,241,0.2)",
    cursor: "pointer",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: "8px 14px",
    borderRadius: 10,
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "#f8fafc",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.08)",
    minWidth: 260,
    flex: 1,
    maxWidth: 420,
  },
  searchInput: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: "0.9rem",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    "&::placeholder": {
      color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
    },
  },
  filterGroup: {
    display: "flex",
    gap: 2,
    padding: 3,
    borderRadius: 10,
    background: isDark(theme) ? "rgba(255,255,255,0.04)" : "#f1f5f9",
  },
  filterChip: {
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: "0.8rem",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    background: "transparent",
    color: isDark(theme) ? "rgba(255,255,255,0.65)" : "#64748b",
    transition: "all 0.15s ease",
    "&:hover": {
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
  },
  filterChipActive: {
    background: isDark(theme) ? "rgba(255,255,255,0.08)" : "#FFFFFF",
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    boxShadow: isDark(theme)
      ? "0 1px 3px rgba(0,0,0,0.25)"
      : "0 1px 3px rgba(15,23,42,0.08)",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(8, 3),
    borderRadius: 16,
    border: isDark(theme)
      ? "1px dashed rgba(255,255,255,0.12)"
      : "1px dashed rgba(15,23,42,0.12)",
    background: isDark(theme) ? "rgba(255,255,255,0.02)" : "#fafbfc",
  },
  emptyIcon: {
    marginBottom: theme.spacing(2),
    color: isDark(theme) ? "rgba(255,255,255,0.35)" : "#cbd5e1",
  },
  emptyTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    marginBottom: theme.spacing(0.5),
  },
  emptyText: {
    fontSize: "0.88rem",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
    maxWidth: 380,
    margin: "0 auto",
    lineHeight: 1.5,
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(8),
    color: isDark(theme) ? "rgba(255,255,255,0.4)" : "#94a3b8",
  },
  // table / cards
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: theme.spacing(2),
  },
  card: {
    position: "relative",
    padding: theme.spacing(2.5),
    borderRadius: 14,
    background: isDark(theme) ? "rgba(255,255,255,0.03)" : "#FFFFFF",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.08)",
    transition: "border-color 0.15s ease, transform 0.15s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    "&:hover": {
      borderColor: isDark(theme) ? "rgba(99,102,241,0.35)" : "rgba(79,70,229,0.3)",
      transform: "translateY(-2px)",
      boxShadow: isDark(theme)
        ? "0 8px 24px rgba(0,0,0,0.25)"
        : "0 8px 24px rgba(15,23,42,0.08)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  cardTitleBlock: {
    minWidth: 0,
    flex: 1,
  },
  cardName: {
    fontSize: "1rem",
    fontWeight: 600,
    color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    letterSpacing: "-0.01em",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontFamily:
      "ui-monospace, 'SF Mono', 'Menlo', 'Consolas', 'Liberation Mono', monospace",
  },
  cardMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
  },
  cardCategory: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: isDark(theme) ? "rgba(255,255,255,0.55)" : "#64748b",
  },
  cardDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: isDark(theme) ? "rgba(255,255,255,0.25)" : "rgba(15,23,42,0.25)",
  },
  cardLang: {
    fontSize: "0.7rem",
    fontWeight: 500,
    color: isDark(theme) ? "rgba(255,255,255,0.45)" : "#94a3b8",
  },
  cardBody: {
    fontSize: "0.88rem",
    color: isDark(theme) ? "rgba(255,255,255,0.72)" : "#334155",
    lineHeight: 1.45,
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    minHeight: "3.8em",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing(1.5),
    borderTop: isDark(theme)
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid rgba(15,23,42,0.06)",
  },
  actionRow: {
    display: "flex",
    gap: 4,
  },
  iconBtn: {
    width: 30,
    height: 30,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: isDark(theme) ? "rgba(255,255,255,0.6)" : "#64748b",
    transition: "background 0.15s ease, color 0.15s ease",
    "&:hover": {
      background: isDark(theme) ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.05)",
      color: isDark(theme) ? "#FFFFFF" : "#0f172a",
    },
  },
  iconBtnDanger: {
    "&:hover": {
      background: isDark(theme) ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
      color: isDark(theme) ? "#fca5a5" : "#dc2626",
    },
  },
  // status pills
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    flexShrink: 0,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
  },
  popoverMenu: {
    position: "absolute",
    top: 44,
    right: 12,
    minWidth: 210,
    padding: 6,
    borderRadius: 12,
    background: isDark(theme) ? "#1e293b" : "#FFFFFF",
    border: isDark(theme)
      ? "1px solid rgba(255,255,255,0.08)"
      : "1px solid rgba(15,23,42,0.08)",
    boxShadow: isDark(theme)
      ? "0 12px 32px rgba(0,0,0,0.5)"
      : "0 12px 32px rgba(15,23,42,0.12)",
    zIndex: 5,
  },
  popoverItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: "9px 12px",
    borderRadius: 8,
    cursor: "pointer",
    fontSize: "0.86rem",
    color: isDark(theme) ? "rgba(255,255,255,0.85)" : "#334155",
    "&:hover": {
      background: isDark(theme) ? "rgba(255,255,255,0.06)" : "#f1f5f9",
    },
  },
  popoverItemDanger: {
    color: isDark(theme) ? "#fca5a5" : "#dc2626",
  },
}));
